/**
 * SchoolAttendance — Full-featured attendance management
 * Tabs: Manual Roll Call | Online Class | Catch-Up | At-Risk | History | Settings
 */
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useAcademicContext } from '@/components/school/AcademicContext';
import {
  Check, X, Clock, Loader2, UserCheck, UserX, AlertCircle, CalendarDays,
  Video, BookOpen, AlertTriangle, Settings, Plus, Play, Send, Eye,
  Monitor, RefreshCw, ChevronRight
} from 'lucide-react';
import api from '@/lib/api';
import { CampusHolidayBanner } from '@/components/school/CampusContext';
import { MediaUpload } from '@/components/MediaUpload';

const STATUS_COLORS = {
  present: 'bg-green-100 text-green-700 hover:bg-green-200',
  absent: 'bg-red-100 text-red-700 hover:bg-red-200',
  late: 'bg-amber-100 text-amber-700 hover:bg-amber-200',
  excused: 'bg-blue-100 text-blue-700 hover:bg-blue-200',
};

// ========== MANUAL ROLL CALL ==========
const ManualRollCall = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const { termId: attTermId } = useAcademicContext(tenantId);
  const today = new Date().toISOString().split('T')[0];
  // Multi-select: choose one or many classes across one or many campuses.
  // Empty selectedCampuses = "any campus" (all classes offered).
  const [selectedCampuses, setSelectedCampuses] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [selectedDate, setSelectedDate] = useState(today);
  const [records, setRecords] = useState({});
  const [notes, setNotes] = useState({});

  const { data: classes = [] } = useQuery({
    queryKey: ['school-classes', tenantId],
    queryFn: async () => { const { data } = await api.get(`/school/tenants/${tenantId}/classes`); return data; },
  });
  const { data: campusesData } = useQuery({
    queryKey: ['school-campuses', tenantId],
    queryFn: async () => { const { data } = await api.get(`/school/tenants/${tenantId}/campuses`); return data; },
  });
  const campuses = campusesData?.items || [];

  // Derive which classes satisfy the campus filter (if any)
  const classesInFilter = React.useMemo(() => {
    if (selectedCampuses.length === 0) return classes;
    return classes.filter((c) => {
      const cid = c.campus_id || '';
      // 'shared' pseudo-id = tenant-wide (no campus)
      return selectedCampuses.includes(cid) || (!cid && selectedCampuses.includes('shared'));
    });
  }, [classes, selectedCampuses]);

  // Fetch attendance for every selected class and merge
  const attendanceQueries = useQuery({
    queryKey: ['school-attendance-multi', tenantId, selectedClasses.join(','), selectedDate],
    queryFn: async () => {
      if (selectedClasses.length === 0) return { records: [] };
      const lists = await Promise.all(selectedClasses.map(async (cid) => {
        const { data } = await api.get(`/school/tenants/${tenantId}/attendance?class_id=${cid}&date=${selectedDate}`);
        const className = (classes.find((c) => c.id === cid) || {}).name || cid;
        return (data?.records || []).map((r) => ({ ...r, _class_id: cid, _class_name: className }));
      }));
      return { records: lists.flat() };
    },
    enabled: selectedClasses.length > 0,
  });
  const attendanceData = attendanceQueries.data;
  const isLoading = attendanceQueries.isLoading;

  const { data: summary } = useQuery({
    queryKey: ['attendance-summary', tenantId, selectedClasses[0] || ''],
    queryFn: async () => {
      const primary = selectedClasses[0];
      const url = primary
        ? `/school/tenants/${tenantId}/attendance/summary?class_id=${primary}`
        : `/school/tenants/${tenantId}/attendance/summary`;
      const { data } = await api.get(url);
      return data;
    },
  });

  useEffect(() => {
    if (attendanceData?.records) {
      const init = {};
      const initNotes = {};
      attendanceData.records.forEach((r) => {
        init[r.id] = r.status || 'present';
        if (r.notes) initNotes[r.id] = r.notes;
      });
      setRecords(init);
      setNotes(initNotes);
    }
  }, [attendanceData]);

  const markMutation = useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.post(`/school/tenants/${tenantId}/attendance`,
        { ...payload, term_id: payload.term_id || attTermId || null });
      return data;
    },
    onSuccess: () => {
      toast.success('Attendance saved across selected classes');
      queryClient.invalidateQueries({ queryKey: ['school-attendance-multi'] });
      queryClient.invalidateQueries({ queryKey: ['attendance-summary'] });
    },
    onError: (e) => toast.error(e.message),
  });

  const handleMarkAll = (status) => {
    if (!attendanceData?.records) return;
    const updated = {};
    attendanceData.records.forEach((r) => { updated[r.id] = status; });
    setRecords(updated);
  };

  const handleSave = () => {
    if (selectedClasses.length === 0 || !attendanceData?.records) return;
    // One POST per class so backend existing contract is respected.
    const byClass = {};
    attendanceData.records.forEach((r) => {
      if (!byClass[r._class_id]) byClass[r._class_id] = [];
      byClass[r._class_id].push({ student_id: r.id, status: records[r.id] || 'present', notes: notes[r.id] || null });
    });
    Object.entries(byClass).forEach(([cid, recs]) => {
      markMutation.mutate({ class_id: cid, date: selectedDate, records: recs });
    });
  };

  const toggleCampus = (id) => setSelectedCampuses((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const toggleClass = (id) => setSelectedClasses((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const allClassIdsInFilter = classesInFilter.map((c) => c.id);
  const allSelected = allClassIdsInFilter.length > 0 && allClassIdsInFilter.every((id) => selectedClasses.includes(id));
  const toggleAllClasses = () => {
    if (allSelected) setSelectedClasses((prev) => prev.filter((id) => !allClassIdsInFilter.includes(id)));
    else setSelectedClasses((prev) => Array.from(new Set([...prev, ...allClassIdsInFilter])));
  };

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Rate', value: `${summary?.attendance_rate || 0}%`, color: 'text-slate-900' },
          { label: 'Present', value: summary?.present || 0, color: 'text-green-600' },
          { label: 'Absent', value: summary?.absent || 0, color: 'text-red-600' },
          { label: 'Late', value: summary?.late || 0, color: 'text-amber-600' },
          { label: 'Total', value: summary?.total_records || 0, color: 'text-slate-600' },
        ].map(s => (
          <Card key={s.label}><CardContent className="p-3 text-center">
            <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-xs text-slate-500">{s.label}</p>
          </CardContent></Card>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><CalendarDays className="h-4 w-4" /> Manual Roll Call</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-start gap-3 mb-4">
            {/* Campus multi-select (only rendered when campuses exist) */}
            {campuses.length > 0 && (
              <div className="min-w-0 sm:min-w-[220px]">
                <Label className="text-xs">Campuses ({selectedCampuses.length || 'any'})</Label>
                <div className="border rounded p-2 max-h-36 overflow-y-auto bg-white" data-testid="attendance-campus-filter">
                  {campuses.map((c) => (
                    <label key={c.id} className="flex items-center gap-2 text-xs py-0.5 cursor-pointer">
                      <input type="checkbox" checked={selectedCampuses.includes(c.id)} onChange={() => toggleCampus(c.id)}
                             data-testid={`attendance-campus-${c.id}`} />
                      {c.name}
                    </label>
                  ))}
                  <label className="flex items-center gap-2 text-xs py-0.5 cursor-pointer border-t pt-1 mt-1">
                    <input type="checkbox" checked={selectedCampuses.includes('shared')} onChange={() => toggleCampus('shared')} />
                    <em className="text-slate-500">Shared (no campus)</em>
                  </label>
                </div>
              </div>
            )}
            {/* Class multi-select — respects campus filter */}
            <div className="min-w-0 sm:min-w-[240px]">
              <Label className="text-xs flex items-center justify-between">
                <span>Classes ({selectedClasses.length})</span>
                {classesInFilter.length > 0 && (
                  <button type="button" onClick={toggleAllClasses} className="text-[10px] text-indigo-600 hover:underline" data-testid="attendance-toggle-all">
                    {allSelected ? 'Clear all' : 'Select all'}
                  </button>
                )}
              </Label>
              <div className="border rounded p-2 max-h-36 overflow-y-auto bg-white" data-testid="attendance-class-filter">
                {classesInFilter.length === 0 ? (
                  <p className="text-xs text-slate-400">No classes match campus filter.</p>
                ) : classesInFilter.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-xs py-0.5 cursor-pointer">
                    <input type="checkbox" checked={selectedClasses.includes(c.id)} onChange={() => toggleClass(c.id)}
                           data-testid={`attendance-class-${c.id}`} />
                    {c.name}
                    {c.campus_id && campuses.length > 0 && (
                      <span className="text-[10px] text-slate-400">· {(campuses.find((cc) => cc.id === c.campus_id) || {}).name || 'campus'}</span>
                    )}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Date</Label>
              <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="w-full max-w-[160px]" data-testid="attendance-date-input" />
            </div>
            {selectedClasses.length > 0 && (
              <div className="flex gap-1.5 items-end">
                <Button variant="outline" size="sm" onClick={() => handleMarkAll('present')} data-testid="mark-all-present-btn"><UserCheck className="h-3.5 w-3.5 mr-1" /> All Present</Button>
                <Button variant="outline" size="sm" onClick={() => handleMarkAll('absent')}><UserX className="h-3.5 w-3.5 mr-1" /> All Absent</Button>
              </div>
            )}
          </div>

          {selectedClasses.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8" data-testid="attendance-no-classes">Select one or more classes (across any campuses) to start marking. Tick multiple to combine the rosters.</p>
          ) : isLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : !attendanceData?.records?.length ? (
            <p className="text-sm text-slate-400 text-center py-8">No students enrolled in the selected classes</p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">#</TableHead>
                    <TableHead>Student</TableHead>
                    <TableHead>Class</TableHead>
                    <TableHead>Adm #</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {attendanceData.records.map((student, idx) => {
                    const status = records[student.id] || 'present';
                    return (
                      <TableRow key={`${student._class_id}-${student.id}`}>
                        <TableCell className="text-slate-400 text-xs">{idx + 1}</TableCell>
                        <TableCell className="font-medium text-sm">{student.first_name} {student.last_name}</TableCell>
                        <TableCell className="text-xs text-slate-500">{student._class_name}</TableCell>
                        <TableCell className="text-xs text-slate-500">{student.admission_number}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {['present', 'absent', 'late', 'excused'].map((s) => (
                              <button key={s} onClick={() => setRecords((prev) => ({ ...prev, [student.id]: s }))}
                                className={`px-2 py-0.5 rounded text-xs font-medium capitalize transition-colors ${status === s ? STATUS_COLORS[s] : 'bg-slate-50 text-slate-400 hover:bg-slate-100'}`}
                                data-testid={`attendance-${student.id}-${s}`}>{s}</button>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Input className="h-7 text-xs" placeholder="Optional note..."
                            value={notes[student.id] || ''} onChange={(e) => setNotes((prev) => ({ ...prev, [student.id]: e.target.value }))} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <div className="flex justify-end mt-3">
                <Button onClick={handleSave} disabled={markMutation.isPending} data-testid="save-attendance-btn">
                  {markMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Save Attendance
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ========== ONLINE CLASS ATTENDANCE ==========
const OnlineClassAttendance = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [showJoinEvent, setShowJoinEvent] = useState(null);
  const [form, setForm] = useState({ class_id: '', platform: 'zoom', meeting_id: '', meeting_url: '', title: '', scheduled_start: '', scheduled_end: '', date: new Date().toISOString().split('T')[0] });
  const [joinForm, setJoinForm] = useState({ student_id: '', platform: 'zoom', meeting_id: '', joined_at: '', left_at: '' });

  const { data: classes = [] } = useQuery({
    queryKey: ['school-classes', tenantId],
    queryFn: async () => { const { data } = await api.get(`/school/tenants/${tenantId}/classes`); return data; },
  });

  const { data: students } = useQuery({
    queryKey: ['school-students-online', tenantId],
    queryFn: async () => { const { data } = await api.get(`/school/tenants/${tenantId}/students?limit=200`); return data; },
  });

  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ['online-sessions', tenantId],
    queryFn: async () => { const { data } = await api.get(`/school/tenants/${tenantId}/attendance/online-sessions`); return data; },
  });

  const createMutation = useMutation({
    mutationFn: async (d) => { const { data } = await api.post(`/school/tenants/${tenantId}/attendance/online-sessions`, d); return data; },
    onSuccess: () => { toast.success('Online session created'); setShowCreate(false); queryClient.invalidateQueries({ queryKey: ['online-sessions'] }); },
    onError: (e) => toast.error(e.message),
  });

  const joinMutation = useMutation({
    mutationFn: async ({ sessionId, data: d }) => { const { data } = await api.post(`/school/tenants/${tenantId}/attendance/online-sessions/${sessionId}/join-events`, d); return data; },
    onSuccess: () => { toast.success('Join event recorded'); setShowJoinEvent(null); queryClient.invalidateQueries({ queryKey: ['online-sessions'] }); },
    onError: (e) => toast.error(e.message),
  });

  const processMutation = useMutation({
    mutationFn: async (sessionId) => { const { data } = await api.post(`/school/tenants/${tenantId}/attendance/online-sessions/${sessionId}/process`); return data; },
    onSuccess: (d) => { toast.success(`Processed: ${d.present} present, ${d.late} late, ${d.absent} absent`); queryClient.invalidateQueries({ queryKey: ['online-sessions'] }); queryClient.invalidateQueries({ queryKey: ['school-attendance'] }); },
    onError: (e) => toast.error(e.message),
  });

  const studentList = students?.students || [];

  return (
    <div className="space-y-4" data-testid="online-attendance">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-900">Online Class Sessions</h3>
          <p className="text-xs text-slate-500">Track attendance from Zoom, Google Meet, Teams, and other platforms</p>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild><Button size="sm" data-testid="create-online-session-btn"><Plus className="h-4 w-4 mr-1" /> Add Session</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Create Online Class Session</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div>
                <Label>Class *</Label>
                <Select value={form.class_id} onValueChange={v => setForm({ ...form, class_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Platform *</Label>
                <Select value={form.platform} onValueChange={v => setForm({ ...form, platform: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="zoom">Zoom</SelectItem>
                    <SelectItem value="gmeet">Google Meet</SelectItem>
                    <SelectItem value="teams">MS Teams</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Math Lesson 1" /></div>
              <div><Label>Meeting ID</Label><Input value={form.meeting_id} onChange={e => setForm({ ...form, meeting_id: e.target.value })} placeholder="123-456-789" /></div>
              <div><Label>Meeting URL</Label><Input value={form.meeting_url} onChange={e => setForm({ ...form, meeting_url: e.target.value })} placeholder="https://..." /></div>
              <div><Label>Date *</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
              <div><Label>Scheduled Start *</Label><Input type="datetime-local" value={form.scheduled_start} onChange={e => setForm({ ...form, scheduled_start: e.target.value })} /></div>
              <div><Label>Scheduled End *</Label><Input type="datetime-local" value={form.scheduled_end} onChange={e => setForm({ ...form, scheduled_end: e.target.value })} /></div>
            </div>
            <DialogFooter className="mt-3">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={() => createMutation.mutate(form)} disabled={createMutation.isPending || !form.class_id || !form.title || !form.scheduled_start} data-testid="online-session-submit">
                {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Join event dialog */}
      <Dialog open={!!showJoinEvent} onOpenChange={() => setShowJoinEvent(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record Join/Leave Event</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label>Student *</Label>
              <Select value={joinForm.student_id} onValueChange={v => setJoinForm({ ...joinForm, student_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select student" /></SelectTrigger>
                <SelectContent>{studentList.map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Joined At *</Label><Input type="datetime-local" value={joinForm.joined_at} onChange={e => setJoinForm({ ...joinForm, joined_at: e.target.value })} /></div>
              <div><Label>Left At</Label><Input type="datetime-local" value={joinForm.left_at} onChange={e => setJoinForm({ ...joinForm, left_at: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => setShowJoinEvent(null)}>Cancel</Button>
            <Button onClick={() => joinMutation.mutate({ sessionId: showJoinEvent, data: { ...joinForm, platform: 'manual', meeting_id: showJoinEvent } })} disabled={joinMutation.isPending || !joinForm.student_id || !joinForm.joined_at} data-testid="join-event-submit">
              {joinMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Class</TableHead>
              <TableHead>Platform</TableHead>
              <TableHead>Participants</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? <TableRow><TableCell colSpan={7} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></TableCell></TableRow> :
            sessions.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">No online sessions yet</TableCell></TableRow> :
            sessions.map(s => (
              <TableRow key={s.id} data-testid={`online-session-${s.id}`}>
                <TableCell className="text-sm">{s.date}</TableCell>
                <TableCell className="font-medium text-sm">{s.title}</TableCell>
                <TableCell className="text-sm">{s.class_name}</TableCell>
                <TableCell><Badge variant="outline" className="capitalize text-xs">{s.platform}</Badge></TableCell>
                <TableCell className="text-sm">{s.participants || 0}</TableCell>
                <TableCell><Badge variant={s.status === 'processed' ? 'default' : 'secondary'} className={s.status === 'processed' ? 'bg-green-100 text-green-700' : ''}>{s.status}</Badge></TableCell>
                <TableCell className="text-right space-x-1">
                  <Button variant="ghost" size="sm" onClick={() => { setJoinForm({ student_id: '', platform: s.platform, meeting_id: s.meeting_id || '', joined_at: '', left_at: '' }); setShowJoinEvent(s.id); }} data-testid={`add-join-event-${s.id}`}>
                    <UserCheck className="h-3.5 w-3.5" />
                  </Button>
                  {s.status !== 'processed' && (
                    <Button variant="outline" size="sm" onClick={() => processMutation.mutate(s.id)} disabled={processMutation.isPending} data-testid={`process-session-${s.id}`}>
                      <RefreshCw className="h-3.5 w-3.5 mr-1" /> Process
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

// ========== CATCH-UP CLASSES ==========
const CatchUpClasses = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ class_id: '', title: '', video_url: '', original_date: '', duration_minutes: 60, quiz_questions: [] });
  const [quizQ, setQuizQ] = useState({ question: '', correct_answer: '', options: '' });

  const { data: classes = [] } = useQuery({
    queryKey: ['school-classes', tenantId],
    queryFn: async () => { const { data } = await api.get(`/school/tenants/${tenantId}/classes`); return data; },
  });

  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ['catchup-sessions', tenantId],
    queryFn: async () => { const { data } = await api.get(`/school/tenants/${tenantId}/attendance/catchup-sessions`); return data; },
  });

  const createMutation = useMutation({
    mutationFn: async (d) => { const { data } = await api.post(`/school/tenants/${tenantId}/attendance/catchup-sessions`, d); return data; },
    onSuccess: () => { toast.success('Catch-up session created'); setShowCreate(false); setForm({ class_id: '', title: '', video_url: '', original_date: '', duration_minutes: 60, quiz_questions: [] }); queryClient.invalidateQueries({ queryKey: ['catchup-sessions'] }); },
    onError: (e) => toast.error(e.message),
  });

  const addQuestion = () => {
    if (!quizQ.question || !quizQ.correct_answer) return;
    setForm(prev => ({
      ...prev,
      quiz_questions: [...prev.quiz_questions, { question: quizQ.question, correct_answer: quizQ.correct_answer, options: quizQ.options.split(',').map(o => o.trim()).filter(Boolean) }]
    }));
    setQuizQ({ question: '', correct_answer: '', options: '' });
  };

  return (
    <div className="space-y-4" data-testid="catchup-attendance">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-900">Catch-Up Classes</h3>
          <p className="text-xs text-slate-500">Recorded lessons with watch tracking and verification quiz</p>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild><Button size="sm" data-testid="create-catchup-btn"><Plus className="h-4 w-4 mr-1" /> Add Catch-Up</Button></DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Create Catch-Up Session</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Class *</Label>
                  <Select value={form.class_id} onValueChange={v => setForm({ ...form, class_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Original Date *</Label><Input type="date" value={form.original_date} onChange={e => setForm({ ...form, original_date: e.target.value })} /></div>
              </div>
              <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Math Lesson 5 - Catch Up" /></div>
              <div><Label>Video link (YouTube / Vimeo) *</Label><Input value={form.video_url} onChange={e => setForm({ ...form, video_url: e.target.value })} placeholder="https://youtube.com/watch?v=..." /></div>
              <MediaUpload value={/^https?:\/\//i.test(form.video_url || '') ? '' : form.video_url} onChange={(url) => setForm({ ...form, video_url: url })} purpose="video" label="…or upload the recording" testId="catchup-video-upload" />
              <div><Label>Duration (minutes)</Label><Input type="number" value={form.duration_minutes} onChange={e => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 60 })} /></div>

              {/* Quiz builder */}
              <div className="border rounded-lg p-3 space-y-2">
                <p className="font-medium text-sm">Verification Quiz ({form.quiz_questions.length} questions)</p>
                {form.quiz_questions.map((q, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs bg-slate-50 p-2 rounded">
                    <span className="font-medium">Q{i + 1}:</span> {q.question}
                    <Badge variant="outline" className="ml-auto text-xs">Answer: {q.correct_answer}</Badge>
                    <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => setForm(prev => ({ ...prev, quiz_questions: prev.quiz_questions.filter((_, j) => j !== i) }))}><X className="h-3 w-3" /></Button>
                  </div>
                ))}
                <div className="space-y-1.5">
                  <Input className="h-8 text-sm" placeholder="Question..." value={quizQ.question} onChange={e => setQuizQ({ ...quizQ, question: e.target.value })} />
                  <div className="grid grid-cols-2 gap-2">
                    <Input className="h-8 text-sm" placeholder="Correct answer" value={quizQ.correct_answer} onChange={e => setQuizQ({ ...quizQ, correct_answer: e.target.value })} />
                    <Input className="h-8 text-sm" placeholder="Options (comma separated)" value={quizQ.options} onChange={e => setQuizQ({ ...quizQ, options: e.target.value })} />
                  </div>
                  <Button variant="outline" size="sm" onClick={addQuestion} disabled={!quizQ.question || !quizQ.correct_answer}><Plus className="h-3 w-3 mr-1" /> Add Question</Button>
                </div>
              </div>
            </div>
            <DialogFooter className="mt-3">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={() => createMutation.mutate(form)} disabled={createMutation.isPending || !form.class_id || !form.title || !form.video_url} data-testid="catchup-submit">
                {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Class</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>Quiz</TableHead>
              <TableHead>Completions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? <TableRow><TableCell colSpan={6} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></TableCell></TableRow> :
            sessions.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No catch-up sessions yet</TableCell></TableRow> :
            sessions.map(s => (
              <TableRow key={s.id} data-testid={`catchup-session-${s.id}`}>
                <TableCell className="text-sm">{s.original_date}</TableCell>
                <TableCell className="font-medium text-sm">{s.title}</TableCell>
                <TableCell className="text-sm">{s.class_name}</TableCell>
                <TableCell className="text-sm">{s.duration_minutes} min</TableCell>
                <TableCell><Badge variant={s.quiz_questions?.length > 0 ? 'default' : 'secondary'} className={s.quiz_questions?.length > 0 ? 'bg-indigo-100 text-indigo-700' : ''}>{s.quiz_questions?.length || 0} Q</Badge></TableCell>
                <TableCell><Badge variant="outline">{s.completions || 0}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

// ========== AT-RISK STUDENTS ==========
const AtRiskStudents = ({ tenantId }) => {
  const queryClient = useQueryClient();

  const { data: alerts = [], isLoading } = useQuery({
    queryKey: ['at-risk', tenantId],
    queryFn: async () => { const { data } = await api.get(`/school/tenants/${tenantId}/attendance/at-risk`); return data; },
  });

  const scanMutation = useMutation({
    mutationFn: async () => { const { data } = await api.post(`/school/tenants/${tenantId}/attendance/at-risk/scan`); return data; },
    onSuccess: (d) => { toast.success(d.message); setTimeout(() => queryClient.invalidateQueries({ queryKey: ['at-risk'] }), 3000); },
    onError: (e) => toast.error(e.message),
  });

  const ackMutation = useMutation({
    mutationFn: async (alertId) => { const { data } = await api.post(`/school/tenants/${tenantId}/attendance/at-risk/${alertId}/acknowledge`); return data; },
    onSuccess: () => { toast.success('Alert acknowledged'); queryClient.invalidateQueries({ queryKey: ['at-risk'] }); },
  });

  return (
    <div className="space-y-4" data-testid="at-risk-students">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-900">At-Risk Students</h3>
          <p className="text-xs text-slate-500">Students below minimum attendance threshold</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => scanMutation.mutate()} disabled={scanMutation.isPending} data-testid="scan-at-risk-btn">
          {scanMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />} Scan All Students
        </Button>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Student</TableHead>
              <TableHead>Attendance Rate</TableHead>
              <TableHead>Threshold</TableHead>
              <TableHead>Records</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? <TableRow><TableCell colSpan={6} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></TableCell></TableRow> :
            alerts.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">
                <AlertTriangle className="h-5 w-5 mx-auto mb-1 text-green-400" />No at-risk students
              </TableCell></TableRow>
            ) :
            alerts.map(a => (
              <TableRow key={a.id} data-testid={`at-risk-${a.id}`}>
                <TableCell className="font-medium text-sm">{a.student_name}</TableCell>
                <TableCell>
                  <span className={`font-bold ${a.attendance_rate < 50 ? 'text-red-600' : 'text-amber-600'}`}>{a.attendance_rate}%</span>
                </TableCell>
                <TableCell className="text-sm">{a.threshold}%</TableCell>
                <TableCell className="text-sm">{a.present_count}/{a.total_records}</TableCell>
                <TableCell><Badge variant={a.status === 'active' ? 'destructive' : 'secondary'}>{a.status}</Badge></TableCell>
                <TableCell className="text-right">
                  {a.status === 'active' && (
                    <Button variant="outline" size="sm" onClick={() => ackMutation.mutate(a.id)} data-testid={`ack-alert-${a.id}`}>Acknowledge</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

// ========== ATTENDANCE SETTINGS ==========
const AttendanceSettingsTab = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const [settings, setSettings] = useState(null);

  const { data: settingsData } = useQuery({
    queryKey: ['attendance-settings', tenantId],
    queryFn: async () => { const { data } = await api.get(`/school/tenants/${tenantId}/attendance/settings`); return data; },
  });

  useEffect(() => { if (settingsData) setSettings(settingsData); }, [settingsData]);

  const saveMutation = useMutation({
    mutationFn: async (d) => { const { data } = await api.put(`/school/tenants/${tenantId}/attendance/settings`, d); return data; },
    onSuccess: () => { toast.success('Settings saved'); queryClient.invalidateQueries({ queryKey: ['attendance-settings'] }); },
    onError: (e) => toast.error(e.message),
  });

  if (!settings) return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  const toggleChannel = (ch) => {
    const channels = settings.notify_channels || [];
    setSettings({ ...settings, notify_channels: channels.includes(ch) ? channels.filter(c => c !== ch) : [...channels, ch] });
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="attendance-settings">
      <Card>
        <CardHeader><CardTitle className="text-base">Attendance Thresholds</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-slate-500">Define how online attendance is classified based on when a student joins relative to scheduled start time.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div>
              <Label>Present (within minutes)</Label>
              <Input type="number" value={settings.present_threshold_minutes} onChange={e => setSettings({ ...settings, present_threshold_minutes: parseInt(e.target.value) || 0 })} data-testid="present-threshold" />
              <p className="text-xs text-slate-400 mt-1">Student arrives within this many minutes of start</p>
            </div>
            <div>
              <Label>Late (within minutes)</Label>
              <Input type="number" value={settings.late_threshold_minutes} onChange={e => setSettings({ ...settings, late_threshold_minutes: parseInt(e.target.value) || 0 })} data-testid="late-threshold" />
              <p className="text-xs text-slate-400 mt-1">After "present" but before this = late</p>
            </div>
            <div>
              <Label>Absent (after minutes)</Label>
              <Input type="number" value={settings.absent_threshold_minutes} onChange={e => setSettings({ ...settings, absent_threshold_minutes: parseInt(e.target.value) || 0 })} data-testid="absent-threshold" />
              <p className="text-xs text-slate-400 mt-1">After this threshold = absent</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">At-Risk Detection</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Minimum Attendance Rate (%)</Label>
            <Input type="number" value={settings.minimum_attendance_rate} onChange={e => setSettings({ ...settings, minimum_attendance_rate: parseFloat(e.target.value) || 0 })} className="w-32" data-testid="min-rate" />
            <p className="text-xs text-slate-400 mt-1">Alert triggered when a student falls below this rate</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Auto-Notifications</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center justify-between border rounded-lg p-3">
              <div><p className="text-sm font-medium">Notify on Absent</p><p className="text-xs text-slate-400">Auto-send when marked absent</p></div>
              <Switch checked={settings.auto_notify_absent} onCheckedChange={v => setSettings({ ...settings, auto_notify_absent: v })} data-testid="notify-absent-toggle" />
            </div>
            <div className="flex items-center justify-between border rounded-lg p-3">
              <div><p className="text-sm font-medium">Notify on Late</p><p className="text-xs text-slate-400">Auto-send when marked late</p></div>
              <Switch checked={settings.auto_notify_late} onCheckedChange={v => setSettings({ ...settings, auto_notify_late: v })} data-testid="notify-late-toggle" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center justify-between border rounded-lg p-3">
              <div><p className="text-sm font-medium">Notify Student</p></div>
              <Switch checked={settings.notify_student} onCheckedChange={v => setSettings({ ...settings, notify_student: v })} />
            </div>
            <div className="flex items-center justify-between border rounded-lg p-3">
              <div><p className="text-sm font-medium">Notify Parent/Guardian</p></div>
              <Switch checked={settings.notify_parent} onCheckedChange={v => setSettings({ ...settings, notify_parent: v })} />
            </div>
          </div>
          <div>
            <Label className="mb-2 block">Notification Channels</Label>
            <div className="flex gap-2">
              {['internal', 'email', 'sms'].map(ch => (
                <button key={ch} onClick={() => toggleChannel(ch)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium capitalize transition-colors ${(settings.notify_channels || []).includes(ch) ? 'bg-indigo-100 text-indigo-700 border border-indigo-200' : 'bg-slate-50 text-slate-400 border border-slate-200 hover:bg-slate-100'}`}
                  data-testid={`channel-${ch}`}>{ch}</button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Catch-Up Settings</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between border rounded-lg p-3">
            <div><p className="text-sm font-medium">Require Quiz for Catch-Up</p><p className="text-xs text-slate-400">Students must pass a quiz to get attendance credit</p></div>
            <Switch checked={settings.catchup_quiz_required} onCheckedChange={v => setSettings({ ...settings, catchup_quiz_required: v })} data-testid="quiz-required-toggle" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Min Watch Percentage</Label>
              <Input type="number" value={settings.catchup_min_watch_percent} onChange={e => setSettings({ ...settings, catchup_min_watch_percent: parseInt(e.target.value) || 0 })} data-testid="min-watch-percent" />
              <p className="text-xs text-slate-400 mt-1">% of video student must watch</p>
            </div>
            <div>
              <Label>Quiz Pass Percentage</Label>
              <Input type="number" value={settings.catchup_quiz_pass_percent} onChange={e => setSettings({ ...settings, catchup_quiz_pass_percent: parseInt(e.target.value) || 0 })} data-testid="quiz-pass-percent" />
              <p className="text-xs text-slate-400 mt-1">% score needed to pass quiz</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={() => saveMutation.mutate(settings)} disabled={saveMutation.isPending} data-testid="save-settings-btn">
          {saveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Save Settings
        </Button>
      </div>
    </div>
  );
};

// ========== MAIN COMPONENT ==========
export const SchoolAttendance = ({ tenantId }) => {
  return (
    <div data-testid="attendance-panel">
      <CampusHolidayBanner tenantId={tenantId} />
      <Tabs defaultValue="manual" className="w-full">
        <TabsList className="mb-4 flex-wrap h-auto gap-1">
          <TabsTrigger value="manual" className="text-xs" data-testid="tab-manual">
            <CalendarDays className="h-3.5 w-3.5 mr-1" /> Manual Roll Call
          </TabsTrigger>
          <TabsTrigger value="online" className="text-xs" data-testid="tab-online">
            <Monitor className="h-3.5 w-3.5 mr-1" /> Online Class
          </TabsTrigger>
          <TabsTrigger value="catchup" className="text-xs" data-testid="tab-catchup">
            <Play className="h-3.5 w-3.5 mr-1" /> Catch-Up
          </TabsTrigger>
          <TabsTrigger value="atrisk" className="text-xs" data-testid="tab-atrisk">
            <AlertTriangle className="h-3.5 w-3.5 mr-1" /> At-Risk
          </TabsTrigger>
          <TabsTrigger value="settings" className="text-xs" data-testid="tab-settings">
            <Settings className="h-3.5 w-3.5 mr-1" /> Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="manual"><ManualRollCall tenantId={tenantId} /></TabsContent>
        <TabsContent value="online"><OnlineClassAttendance tenantId={tenantId} /></TabsContent>
        <TabsContent value="catchup"><CatchUpClasses tenantId={tenantId} /></TabsContent>
        <TabsContent value="atrisk"><AtRiskStudents tenantId={tenantId} /></TabsContent>
        <TabsContent value="settings"><AttendanceSettingsTab tenantId={tenantId} /></TabsContent>
      </Tabs>
    </div>
  );
};

export default SchoolAttendance;
